import { InsertRowAboveOutlined } from "@ant-design/icons"
import { Button, Flex, Typography } from "antd"
import React, { useCallback } from "react"

import { ImportButton } from "../../../../components/ImportButton.tsx"
import { SearchBar } from "../../../../components/Input/SearchBar.tsx"
import { type PermissionProjectPermissions } from "../../../SettingsPermissions/types/permissions.types.ts"
import { ProjectsLeadApprovalTrigger } from "../../ProjectsLeadApproval/ProjectsLeadApprovalTrigger.tsx"
import { useProjectsViewSwitch } from "../../helpers/projects-view-preference.ts"
import type { ProjectFilters } from "../../redux/projects.types.ts"

const { Title, Text } = Typography

interface ProjectListTableHeaderProps {
  projectPermissions: PermissionProjectPermissions
  search: string | null
  onFilersChange: (data: Partial<ProjectFilters>) => void
  onRefetchProjectsCallback: () => void
  onImport: (file: File) => Promise<void>
  onDownloadTemplate: () => Promise<void>
}

export function ProjectListTableHeader({
  projectPermissions,
  search,
  onFilersChange,
  onRefetchProjectsCallback,
  onImport,
  onDownloadTemplate
}: ProjectListTableHeaderProps) {
  const switchProjectsView = useProjectsViewSwitch()

  const onSearchHandler = useCallback(
    (q: string | null) => onFilersChange({ q }),
    [onFilersChange]
  )

  return (
    <Flex
      justify={"space-between"}
      align={"center"}
      wrap={"wrap"}
      gap={16}
      className={"max-lg:flex-col max-lg:items-start"}
    >
      <Flex vertical className={"min-w-0 max-lg:w-full"}>
        <Title className={"text-start whitespace-nowrap"} level={2}>
          Projects
        </Title>
        <Text type={"secondary"}>
          Manage and track all your projects in one place.
        </Text>
      </Flex>

      <Flex
        gap={8}
        wrap={"wrap"}
        className={"max-lg:w-full max-md:flex-col max-md:items-stretch"}
        style={{ maxWidth: "100%" }}
      >
        <SearchBar
          placeholder="Search projects..."
          defaultValue={search || ""}
          onSearch={onSearchHandler}
          className={"max-md:!min-w-0"}
          style={{ minWidth: 200 }}
        />

        {projectPermissions?.projects?.project_create && (
          <ImportButton
            onImport={onImport}
            onDownloadTemplate={onDownloadTemplate}
          />
        )}

        {projectPermissions?.projects?.project_create && (
          <ProjectsLeadApprovalTrigger
            onRefetchCallback={onRefetchProjectsCallback}
          />
        )}

        <Button
          type={"primary"}
          onClick={() => switchProjectsView("grid")}
          icon={<InsertRowAboveOutlined />}
        >
          View Grid
        </Button>
      </Flex>
    </Flex>
  )
}
